import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { DocumentTextIcon, LockClosedIcon, CheckCircleIcon, DownloadIcon, SearchIcon, ExclamationCircleIcon, SparklesIcon } from '@heroicons/react/outline';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { useNavigate } from 'react-router-dom';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { db } from '../../../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { formatDateTime } from '../../../../utils/timestampUtils';
import { TriggerFetchLatestDocForDownload } from '../../../../utils/TriggerFetchLatestDocForDownload';
import { TriggerSembleMdtReport } from '../../../../utils/TriggerSembleMdtReport';
import { TriggerRenderSembleMdtReport } from '../../../../utils/TriggerRenderSembleMdtReport';
import { isClinicianAssignedToPatient } from '../../../../utils/clinicianPermissions';
import { getSembleMdtReportRowStatus } from '../../../../utils/sembleMdtReportStatus';
import { getPdfAvailability } from '../../../../utils/pdfAvailability';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

/**
 * SembleMdtReport — the "Semble MDT Report" row, rendered INSIDE the
 * Additional Documents panel for Well Life Clinic.
 *
 * The report carries two reviewable AI paragraphs (a condensed clinical-history
 * summary and the patient's goals from the MDT Plan page at the end of the
 * Diagnostic Decision); everything else in the document is rules-based or
 * reused verbatim from already-approved CI paragraphs.  Lifecycle (computed by
 * getSembleMdtReportRowStatus, keyed on DD completion):
 *   grey/locked        → Diagnostic Decision not yet complete (lock icon).
 *   grey/not-generated → DD complete but paragraphs missing (legacy patient or
 *                        staleness reset) — manual Generate button.
 *   amber/processing   → AI paragraphs generating ("Generating...") or the
 *                        approved document rendering ("Finalising...").
 *   violet/review      → "AI outputs ready for review" + Review button.
 *   green/approved     → all paragraphs approved; Preview + Download.
 *   amber/pdf-pending  → approved and rendered, but the PDF export hit a
 *                        transient Drive/Storage fault and is queued for
 *                        background retry ("Preparing PDF").  No actions:
 *                        the sweep will flip it to ready or error on its own.
 *   red/error          → generation/render failed — Try Again button.
 *
 * Generation is normally fired automatically by the DD completion flow; the
 * manual Generate/Try Again affordances make the row self-recoverable when
 * that auto-trigger never ran or failed.
 *
 * The row reads the rendered document's pdfStatus (documents.semblemdtreportReport)
 * via getPdfAvailability, the same way the main Documents Dashboard does, so a
 * "section complete, PDF pending/error" write from the render Cloud Function is
 * shown honestly instead of as a green row with the buttons missing.
 */
export default function SembleMdtReport({ patientId, clinicianId, onPreview }) {
  const { patientDataModel, fetchPatientData } = useContext(PatientDataContext);
  const navigate = useNavigate();

  const [isDownloading, setIsDownloading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('idle');
  const prevProcessingStatusRef = useRef('idle');

  const manageReports = patientDataModel?.dataModel?.['6_manageReports'] || {};
  const ddComplete = patientDataModel?.dataModel?.['4_diagnosticDecision']?.status === 'completed';
  const generated = manageReports.sembleMDTReportGenerated === true;
  const approved = manageReports.sembleMDTReportApproved === true;

  const canManage = useMemo(
    () => isClinicianAssignedToPatient(patientDataModel, clinicianId),
    [patientDataModel, clinicianId]
  );

  const exportDoc = patientDataModel?.documents?.semblemdtreportReport || null;
  const lastGenerated = exportDoc?.lastGenerated || null;
  // 'ready' | 'pending' | 'error' | 'missing' — the PDF side of the rendered
  // document, independent of the paragraph lifecycle flags above.
  const pdfAvailability = getPdfAvailability(exportDoc);

  const isProcessing = processingStatus === 'processing';
  const isError = processingStatus === 'error';

  const displayStatus = getSembleMdtReportRowStatus({
    ddComplete,
    processing: isProcessing,
    error: isError,
    generated,
    approved,
    pdfAvailability,
  });

  /**
   * Real-time listener for the Semble processing status; refreshes patient data
   * when processing completes so the row flips straight to its new state
   * (review-ready paragraphs or a freshly rendered document).
   */
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'patients', patientId),
      (docSnap) => {
        if (!docSnap.exists()) return;
        const mr = docSnap.data()?.dataModel?.['6_manageReports'] || {};
        const newStatus = mr.sembleMDTReportProcessingStatus || 'idle';
        const previousStatus = prevProcessingStatusRef.current;
        prevProcessingStatusRef.current = newStatus;
        setProcessingStatus(newStatus);
        if (previousStatus === 'processing' && newStatus !== 'processing') {
          fetchPatientData(patientId, true);
        }
      },
      (error) => console.error('Error in Semble MDT Report snapshot listener:', error)
    );
    return () => unsubscribe();
  }, [patientId, fetchPatientData]);

  // ── Row styling helpers ───────────────────────────────────────────────────
  // 'pdf-pending' shares the amber "something is happening" treatment with
  // 'processing' — both mean "wait, nothing for you to do" — and differs only
  // in its caption, so the clinician can tell a render from a PDF retry.
  const isAmber = displayStatus === 'processing' || displayStatus === 'pdf-pending';

  const rowBgClass = () => {
    if (isAmber) return 'bg-amber-50 hover:bg-amber-100';
    if (displayStatus === 'error') return 'bg-red-50 hover:bg-red-100';
    if (displayStatus === 'approved') return 'bg-green-50 hover:bg-green-100';
    if (displayStatus === 'ready-for-review') return 'bg-violet-50 hover:bg-violet-100';
    return 'bg-gray-50 hover:bg-gray-100';
  };

  const circleClass = () => {
    if (isAmber) return 'bg-amber-50 border-amber-200 text-amber-700';
    if (displayStatus === 'error') return 'bg-red-50 border-red-200 text-red-700';
    if (displayStatus === 'approved') return 'bg-green-50 border-green-200 text-green-700';
    if (displayStatus === 'ready-for-review') return 'bg-violet-50 border-violet-200 text-violet-700';
    return 'bg-gray-50 border-gray-200 text-gray-700';
  };

  const circleIcon = () => {
    if (isAmber) {
      return (
        <svg className="animate-spin h-5 w-5 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      );
    }
    if (displayStatus === 'error') return <ExclamationCircleIcon className="w-5 h-5 text-red-600" />;
    if (displayStatus === 'approved') return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
    if (displayStatus === 'ready-for-review') return <DocumentTextIcon className="w-5 h-5 text-violet-600" />;
    if (displayStatus === 'not-generated') return <DocumentTextIcon className="w-5 h-5 text-gray-500" />;
    return <LockClosedIcon className="w-5 h-5 text-gray-400" />;
  };

  const caption = () => {
    if (displayStatus === 'error') return 'An error occurred. Please try again.';
    // Same wording as the main Documents Dashboard's pending state so the two
    // surfaces read identically for the same underlying condition.
    if (displayStatus === 'pdf-pending') return 'Preparing PDF';
    if (displayStatus === 'approved') return lastGenerated ? `Last generated on ${formatDateTime(lastGenerated)}` : 'Approved — ready to download';
    if (displayStatus === 'ready-for-review') return 'AI outputs ready for review';
    if (displayStatus === 'processing') return approved ? 'Finalising...' : 'Generating...';
    if (displayStatus === 'not-generated') return 'Ready to generate AI outputs for review';
    return 'Available once the Diagnostic Decision is complete';
  };

  const handleReview = () => navigate(`/patient/${patientId}/review-document-semble-mdt`);
  const handleGenerate = () => TriggerSembleMdtReport({ clinicianId, patientId });

  // Retry after an error: if the paragraphs are already approved the failure
  // was in the document render, so retry the render — regenerating instead
  // would wipe the clinician's approvals.  Otherwise retry generation.
  const handleRetry = () => (
    approved && generated
      ? TriggerRenderSembleMdtReport({ clinicianId, patientId })
      : TriggerSembleMdtReport({ clinicianId, patientId })
  );

  const handleDownload = async (format) => {
    try {
      setIsDownloading(true);
      await TriggerFetchLatestDocForDownload({
        reportStage: 'Semble MDT Report',
        clinicianId,
        patientId,
        fileFormat: format || 'pdf',
      });
    } catch (error) {
      console.error('Error downloading Semble MDT Report:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const DownloadDropdown = () => (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        disabled={isDownloading}
        className="bg-violet-800 text-white hover:bg-violet-700 disabled:bg-violet-400 flex items-center justify-center gap-1 py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
      >
        {isDownloading ? 'Downloading...' : (<><DownloadIcon className="h-4 w-4" />Download<ChevronDownIcon className="h-4 w-4" /></>)}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="min-w-[120px] bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50" sideOffset={5}>
          <DropdownMenu.Item className="px-3 py-2 text-sm text-gray-700 hover:bg-violet-100 hover:text-violet-900 cursor-pointer outline-none" onSelect={(e) => { e.preventDefault(); handleDownload('pdf'); }}>Download PDF</DropdownMenu.Item>
          <DropdownMenu.Item className="px-3 py-2 text-sm text-gray-700 hover:bg-violet-100 hover:text-violet-900 cursor-pointer outline-none" onSelect={(e) => { e.preventDefault(); handleDownload('docx'); }}>Download DOCX</DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );

  return (
    <div className="relative">
      <div className={`w-full flex items-center gap-6 my-1 py-2 px-4 rounded-lg transition-all duration-200 ${rowBgClass()}`}>
        <div className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center ${circleClass()}`}>
          {circleIcon()}
        </div>

        <div className="flex-1 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Semble MDT Report</h3>
            <p className="text-sm italic text-gray-700 mt-1">{caption()}</p>
          </div>

          <div className="flex items-center gap-3">
            {displayStatus === 'not-generated' && canManage && (
              <button onClick={handleGenerate} className="bg-violet-800 text-white hover:bg-violet-700 flex items-center justify-center gap-1 py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200">
                <SparklesIcon className="h-4 w-4" />Generate
              </button>
            )}

            {displayStatus === 'ready-for-review' && canManage && (
              <button onClick={handleReview} className="bg-violet-800 text-white hover:bg-violet-700 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200">
                Review AI Outputs
              </button>
            )}

            {displayStatus === 'approved' && (
              <>
                {canManage && (
                  <button onClick={handleReview} className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200">
                    Review Paragraphs
                  </button>
                )}
                {onPreview && lastGenerated && (
                  <button onClick={() => onPreview('Semble MDT Report', lastGenerated)} className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200">
                    <SearchIcon className="w-4 h-4 mr-1.5" />Preview
                  </button>
                )}
                {lastGenerated && <DownloadDropdown />}
              </>
            )}

            {displayStatus === 'error' && canManage && (
              <button onClick={handleRetry} className="bg-red-600 text-white hover:bg-red-700 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200">
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
